import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { JhiLanguageService } from '../shared/core-utils';
import { ParamContext } from '../shared/service/param.context';
import { ActivatedRoute, Router } from '@angular/router';
import { StudyEditorContext } from './study-editor.context';
import { DatasetModel } from '../shared/dataset/model/dataset.model';
import { DatasetService } from '../shared/dataset/service/dataset.service';
import { DATASET_TYPES_OBSERVATION_IDS, DatasetTypeEnum } from '../shared/dataset/model/dataset-type.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChooseDatasetModalComponent } from './shared/modals/choose-dataset-modal/choose-dataset-modal.component';
import { ImportStudyModalComponent } from './tabs/dataset/import-study/import-study-modal/import-study-modal.component';
import { ExportStudyModalComponent } from './tabs/dataset/export-study/export-study-modal/export-study-modal.component';
import { ExeculateCalculatedVariableModalComponent } from './tabs/dataset/calculated-variable/execulate-calculated-variable-modal/execulate-calculated-variable-modal.component';
import { StudyEditorService } from './study-editor.service';
import { PaginatedResponse, StudyEntryService } from '../shared/study/service/study-entry.service';
import { Principal } from '../shared';
import {
    ADVANCE_STUDY_FOR_PLANTS_PERMISSIONS,
    ADVANCE_STUDY_PERMISSIONS,
    ADVANCES_PERMISSIONS,
    ANALYZE_WITH_DECISION_SUPPORT_PERMISSIONS,
    ANALYZE_WITH_STA_BRAPP_PERMISSIONS,
    ANALYZE_WITH_TRIALDATAEXP_BRAPP_PERMISSIONS,
    CHANGE_PLOT_ENTRY_PERMISSIONS,
    CLOSE_STUDY_PERMISSIONS,
    CREATE_GENOTYPING_SAMPLES_PERMISSIONS,
    CREATE_GEOREFERENCE_PERMSISSION,
    CREATE_LOTS_PERMISSIONS,
    CREATE_PLANTING_LABELS_PERMISSIONS,
    CREATE_SUB_OBSERVATION_UNITS_PERMISSIONS,
    DELETE_FIELD_MAP_PERMSISSION,
    DELETE_STUDY_NOT_OWNED_PERMISSIONS,
    DELETE_STUDY_PERMISSIONS,
    DESIGN_NEW_CROSSES_PERMISSIONS,
    EDIT_GEOREFERENCE_PERMSISSION,
    EXECUTE_CALCULATED_VARIABLES_PERMISSIONS,
    EXPORT_CROSSING_TEMPLATE_PERMISSIONS,
    EXPORT_DESIGN_TEMPLATE_PERMISSIONS,
    EXPORT_STUDY_BOOK_PERMISSIONS,
    EXPORT_STUDY_ENTRIES_PERMISSIONS,
    IMPORT_CROSSES_PERMISSIONS,
    IMPORT_DESIGN_PERMISSIONS,
    LOCK_STUDY_NOT_OWNED_PERMISSIONS,
    LOCK_STUDY_PERMISSIONS,
    MAKE_FIELD_MAP_PERMSISSION,
    MANAGE_PENDING_OBSERVATION_VALUES_PERMISSIONS,
    PREPARE_INVENTORY_PERMISSIONS,
    VIEW_CROSSES_AND_SELECTIONS_PERMISSIONS,
    VIEW_ENVIRONMENT_PERMISSIONS,
    VIEW_EXPERIMENTAL_DESIGN_PERMISSIONS,
    VIEW_FIELD_MAP_PERMSISSION,
    VIEW_GERMPLASM_AND_CHECKS_PERMISSIONS,
    VIEW_INVENTORY_PERMISSIONS,
    VIEW_OBSERVATIONS_PERMISSIONS,
    VIEW_SAMPLE_GENOTYPES_PERMISSIONS,
    VIEW_SAMPLE_LISTS_PERMISSIONS,
    VIEW_SINGLE_SITE_ANALYSIS_RESULTS_PERMISSIONS,
    VIEW_STUDY_SETTINGS_PERMISSIONS,
    VIEW_TREATMENT_FACTORS_PERMISSIONS
} from '../shared/auth/permissions';
import { CropParameterService } from '../shared/crop-parameter/service/crop-parameter.service';
import { CropParameterTypeEnum } from '../shared/crop-parameter/model/crop-parameter-type-enum';
import { AlertService } from '../shared/alert/alert.service';
import { ImportDesignModalComponent } from './tabs/experimental-design/import-design/import-design-modal/import-design-modal.component';
import { FileDownloadHelper } from '../entities/sample/file-download.helper';
import { ExperimentDesignService } from './tabs/experimental-design/experiment-design.service';
import { LabelPrintingType } from '../label-printing/label-printing.component';
import { SubObservationUnitTypeSelectorModalComponent } from './tabs/dataset/sub-observation-unit-dataset-builder/sub-observation-unit-type-selector-modal.component';
import { StudyService } from '../shared/study/service/study.service';
import { AdvanceStudyModalService } from './shared/services/advance-study-modal-service';
import { GermplasmStudySourceService } from '../shared/germplasm/service/germplasm-study-source.service';
import { InventoryService } from '../shared/inventory/service/inventory.service';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';
import { StudyDatasetContext } from './tabs/dataset/study-dataset.context';
import { UrlService } from '../shared/service/url.service';
import { GenotypingSamplesModalService } from './shared/services/genotyping-samples-modal.service';
import { SampleListService } from '../entities/sample/sample-list.service';
import { ChooseEnvironmentModalComponent } from './shared/modals/choose-environment-modal/choose-environment-modal.component';
import { StudyInstanceItem } from './shared/study-instances-table-selector/study-instances-table-selector.component';
import { SampleGenotypeModel } from '../entities/sample/genotype/sample-genotype.model';
import { SampleGenotypeService } from '../entities/sample/genotype/sample-genotype.service';
import { HELP_MANAGE_STUDIES } from '../app.constants';
import { ImportCrossesModalComponent } from './tabs/dataset/import-crosses/import-crosses-modal/import-crosses-modal.component';
import { CrossService } from '../shared/study/service/cross.service';
import { SelectEnvironmentFieldMapModalComponent } from '../study-manager/field-map/select-environment-field-map-modal.component';
import { FieldmapService } from '../field-management/service/fieldmap.service';
import { SingleEnvironmentSelectorModalComponent } from './shared/modals/single-environment-selector-modal/single-environment-selector-modal.component';
import { GeoReferenceModalComponent } from './shared/modals/geo-reference-modal/geo-reference-modal.component';
import { ColumnDataService } from './shared/services/column-data.service';

@Component({
    selector: 'jhi-study-editor',
    templateUrl: './study-editor.component.html',
    styleUrls: ['./study-editor.component.scss']
})
export class StudyEditorComponent implements OnInit, OnDestroy {

    protected readonly VIEW_CROSSES_AND_SELECTIONS_PERMISSIONS = VIEW_CROSSES_AND_SELECTIONS_PERMISSIONS;
    protected readonly VIEW_SAMPLE_GENOTYPES_PERMISSIONS = VIEW_SAMPLE_GENOTYPES_PERMISSIONS;
    protected readonly VIEW_INVENTORY_PERMISSIONS = VIEW_INVENTORY_PERMISSIONS;
    protected readonly HELP_MANAGE_STUDIES = HELP_MANAGE_STUDIES;
    protected readonly DatasetTypeEnum = DatasetTypeEnum;

    readonly SUB_OBSERVATION_DATASETS: DatasetTypeEnum[] = [
        DatasetTypeEnum.PLANT_SUBOBSERVATIONS,
        DatasetTypeEnum.QUADRAT_SUBOBSERVATIONS,
        DatasetTypeEnum.TIME_SERIES_SUBOBSERVATIONS,
        DatasetTypeEnum.CUSTOM_SUBOBSERVATIONS];
    readonly OBSERVATION_DATASETS: DatasetTypeEnum[] = [
        DatasetTypeEnum.PLOT,
        ...this.SUB_OBSERVATION_DATASETS];

    studyId: number;
    helpLink: string;
    returnToManageStudiesURL: string;
    studyName: any;
    DatasetType: DatasetTypeEnum;
    staBrappURL: string;
    dsBrappURL: string;
    tdeExplorerBrapp: string;
    identity: any;
    protected showCrossesAndSelections: any;
    protected showSampleGenotypes: boolean;
    protected showInventory: boolean;
    protected showStudySettings: boolean;
    protected showAnalysis: boolean;
    protected showGermplasmAndChecks: boolean;
    protected showTreatmentFactors: boolean;
    protected showEnvironments: boolean;
    protected showExperimentalDesign: boolean;
    protected showObservations: boolean;
    protected isShowStudyAction: boolean;

    subscriptionManager = new SubscriptionsManager();



    constructor(protected principal: Principal,
                protected paramContext: ParamContext,
                private route: ActivatedRoute,
                protected router: Router,
                private jhiLanguageService: JhiLanguageService,
                public translateService: TranslateService,
                public studyEditorContext: StudyEditorContext,
                private datasetService: DatasetService,
                private modalService: NgbModal,
                private studyService: StudyService,
                private studyEditorService: StudyEditorService,
                private studyEntryService: StudyEntryService,
                private cropParameterService: CropParameterService,
                private alertService: AlertService,
                private fileDownloadHelper: FileDownloadHelper,
                private experimentDesignService: ExperimentDesignService,
                private advanceStudyModalService: AdvanceStudyModalService,
                private germplasmStudySourceService: GermplasmStudySourceService,
                private inventoryService: InventoryService,
                private studyDatasetContext: StudyDatasetContext,
                private genotypingSamplesModalService: GenotypingSamplesModalService,
                private urlService: UrlService,
                private sampleListService: SampleListService,
                private crossService: CrossService,
                private sampleGenotypeService: SampleGenotypeService,
                private fieldmapService: FieldmapService) {
        this.paramContext.readParams();

        if (!this.paramContext.cropName || !this.paramContext.programUUID) {
            return;
        }

        this.principal.identity().then(account => {
            this.identity = account;
        });
        this.cropParameterService.getCropParameterForBrapp(this.paramContext.cropName, this.paramContext.programUUID, CropParameterTypeEnum.STA_BRAPP).toPromise().then(cropParameter => {
            this.staBrappURL = cropParameter.value;
        });
        this.cropParameterService.getCropParameterForBrapp(this.paramContext.cropName, this.paramContext.programUUID, CropParameterTypeEnum.DECISION_SUPPORT_BRAPP).toPromise().then(cropParameter => {
            this.dsBrappURL = cropParameter.value;
        });
        this.cropParameterService.getCropParameterForBrapp(this.paramContext.cropName, this.paramContext.programUUID, CropParameterTypeEnum.TDE_BRAPP_URL).toPromise().then(cropParameter => {
            this.tdeExplorerBrapp = cropParameter.value;
        });
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
        this.studyEditorContext.reset();
    }

    async ngOnInit(): Promise<void> {

        this.subscriptionManager.add = this.route.params.subscribe(async params => {
            this.paramContext.readParams();

            if (!this.paramContext.cropName || !this.paramContext.programUUID) {
                return;
            }

            await this.initializeStudyDetails(Number(params.studyId));

            this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'study-settings');

            if (this.studyEditorContext.hasGeneratedDesign()) {
                this.studyEntryService.getStudyEntriesMetadata(this.studyEditorContext.studyId).toPromise().then(studyEntriesMetadata => {
                    if (studyEntriesMetadata.hasUnassignedEntries) {
                        this.alertService.warning('study-editor.germplasm-checks.study.entry.unassigned.warning');
                    }
                });
            }

            // When study is opened, delete ALL the previously saved columns state.
            ColumnDataService.deleteColumnsStateForAllStorageKeys();
        });

        this.subscriptionManager.add = this.studyEditorService.refresh$.subscribe(async () => {
            await this.initializeStudyDetails(this.studyEditorContext.studyId);

            // When refreshing the study, we need to re-fetch the sample lists.
            const sampleListResponse = await this.sampleListService.getSampleListsInStudy(this.studyId, false).toPromise();
            if (sampleListResponse && sampleListResponse.body) {
                this.studyEditorContext.sampleLists = sampleListResponse.body;
            }

            this.studyEditorService.notifyRefreshCompleted();
        });
    }

    async initializeStudyDetails(studyId: number) {
        this.studyId = studyId;
        // Retrieve the Study Details
        const studyDetailsResponse = await this.studyService.getStudyDetails(this.studyId).toPromise();
        this.studyEditorContext.studyDetails = studyDetailsResponse.body;

        this.studyName = this.studyEditorContext.studyDetails.name;
        this.studyEditorContext.studyId = this.studyEditorContext.studyDetails.id;
        this.studyEditorContext.existingStudyLevelVariableIds = this.studyEditorContext.studyDetails.studySettings.map(studySetting => studySetting.termId);
        this.studyEditorContext.existingEnvironmentLevelVariableIds = this.studyEditorContext.studyDetails.environmentDetails.map(environmentDetail => environmentDetail.termId);

        const studyEntriesCount = await this.studyEntryService.getStudyEntriesCount(this.studyId);
        this.studyEditorContext.germplasmListExists = studyEntriesCount > 0;
        this.studyEditorContext.studyDetails.numberOfEntries = studyEntriesCount;

        // Execute show permission methods only once when the component is initially loaded.
        // This helps to minimize performance issues and reduces visual flicker when switching tabs.
        this.showStudySettings = this.showStudySettingsTab();
        this.showGermplasmAndChecks = this.showGermplasmAndChecksTab();
        this.showTreatmentFactors = this.showTreatmentFactorsTab();
        this.showEnvironments = this.showEnvironmentsTab();
        this.showExperimentalDesign = this.showExperimentalDesignTab();
        this.showObservations = this.principal.hasAnyAuthorityDirect(VIEW_OBSERVATIONS_PERMISSIONS);
        this.isShowStudyAction = this.showStudyAction();

        // Load these tabs asynchronously to minimize performance issues and reduce visual flicker when switching tabs.
        this.datasetService.getDatasets(this.studyId, []).toPromise().then((datasetsResponse) => {
            const datasets = datasetsResponse.body;
            this.studyEditorContext.allDatasets = datasets;
            this.studyEditorContext.observationDatasets = this.filterObservationDatasets(datasetsResponse.body);
        }).finally(() => {
            this.showAnalysis = this.showAnalysisTab();
        });

        this.showCrossesAndSelectionsTab().then((showCrossesAndSelectionsTab: boolean) => {
            this.showCrossesAndSelections = showCrossesAndSelectionsTab;
        });
        this.showInventoryTab().then((showInventoryTab: boolean) => {
            this.showInventory = showInventoryTab;
        });
        this.showSampleGenotypesTab().then((showSampleGenotypesTab: boolean) => {
            this.showSampleGenotypes = showSampleGenotypesTab;
        });
        if (this.principal.hasAnyAuthorityDirect(VIEW_SAMPLE_LISTS_PERMISSIONS)) {
            this.sampleListService.getSampleListsInStudy(this.studyId, false).toPromise().then(sampleListsResponse => {
                this.studyEditorContext.sampleLists = sampleListsResponse.body;
            });
        }
        this.datasetService.getSubObsMetadata(studyId).toPromise().then(subObsMetadata => {
            this.studyEditorContext.maxSubObservationParentUnit = subObsMetadata.maxSubObservationParentUnit;
            this.studyEditorContext.maxSubObservationUnitSets = subObsMetadata.maxSubObservationUnitSets;
        });
    }

    isOpenStudy(): boolean {
        return this.studyEditorContext.studyDetails != null;
    }

    filterObservationDatasets(datasets: DatasetModel[]): DatasetModel[] {
        return datasets.filter(dataset => {
            return this.OBSERVATION_DATASETS.includes(dataset.datasetTypeId);
        });
    }

    importStudyBook() {
        const modalRef = this.showChooseDatasetModalForImport();
        modalRef.result.then((result: DatasetModel) => {
            const importStudyRef = this.modalService.open(ImportStudyModalComponent as Component, { size: 'md', backdrop: 'static' });
            importStudyRef.componentInstance.datasetId = result.datasetId;
            importStudyRef.result.then(() => {
            }, (reason) => {
                if (reason === 'back') {
                    this.importStudyBook();
                }
            });
        });
    }

    createFieldmap() {
        const createFieldmapModalRef  = this.modalService.open(SelectEnvironmentFieldMapModalComponent as Component, { size: 'lg', backdrop: 'static' });
        createFieldmapModalRef.componentInstance.studyId = this.studyEditorContext.studyId;
        createFieldmapModalRef.componentInstance.studyName = this.studyEditorContext.studyDetails.name;
        createFieldmapModalRef.componentInstance.isCreateFieldMap = true;
    }

    viewFieldmap() {
        this.fieldmapService.getEnvironmentFieldmapInfo(this.studyId, null).toPromise().then((fieldmapInfo)=> {
            const envWithFieldMap = fieldmapInfo.filter((info) => info.hasFieldMap);
            if (envWithFieldMap.length) {
                const viewFieldmapModalRef  = this.modalService.open(SelectEnvironmentFieldMapModalComponent as Component, { size: 'lg', backdrop: 'static' });
                viewFieldmapModalRef.componentInstance.studyId = this.studyEditorContext.studyId;
                viewFieldmapModalRef.componentInstance.studyName = this.studyEditorContext.studyDetails.name;
                viewFieldmapModalRef.componentInstance.isViewFieldMap = true;
            } else {
                this.alertService.error('study-editor.field-map.no.field.map.error');
            }
        });

    }

    deleteFieldmap() {
        this.fieldmapService.getEnvironmentFieldmapInfo(this.studyId, null).toPromise().then((fieldmapInfo)=> {
            const envWithFieldMap = fieldmapInfo.filter((info) => info.hasFieldMap);
            if (envWithFieldMap.length) {
                const deleteFieldmapModalRef  = this.modalService.open(SelectEnvironmentFieldMapModalComponent as Component, { size: 'lg', backdrop: 'static' });
                deleteFieldmapModalRef.componentInstance.studyId = this.studyEditorContext.studyId;
                deleteFieldmapModalRef.componentInstance.studyName = this.studyEditorContext.studyDetails.name;
                deleteFieldmapModalRef.componentInstance.isDeleteFieldMap = true;
            } else {
                this.alertService.error('study-editor.field-map.no.field.map.error');
            }
        });
    }

    showGeoJSONModal(isViewGeoJSON: boolean) {
        this.datasetService.getDatasetInstances(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId).toPromise().then((datasetInstances) => {
            let instances = [];
            if (isViewGeoJSON) {
                instances = datasetInstances.filter((instance) => instance.hasGeoJSON);
                if (!instances.length) {
                    this.alertService.error('study-editor.geojson.no.georeference.error');
                    return;
                }
            } else {
                instances = datasetInstances.filter((instance) => instance.hasFieldLayout);
                if (!instances || !instances.length) {
                    this.alertService.error('study-editor.geojson.no.layout.error');
                    return;
                }
                instances = instances.filter((instance) => !instance.hasGeoJSON);
                if (!instances.length) {
                    this.alertService.error('study-editor.geojson.all.instances.georeferenced.error');
                    return;
                }
            }
            const singleEnvironmentSelectModal = this.modalService.open(SingleEnvironmentSelectorModalComponent as Component, { size: 'md', backdrop: 'static' });
            singleEnvironmentSelectModal.componentInstance.studyInstances = instances;
            singleEnvironmentSelectModal.componentInstance.titleKey = 'study-editor.select.an.environment.title';
            singleEnvironmentSelectModal.componentInstance.descriptionKey = 'study-editor.select.an.environment.label';
            singleEnvironmentSelectModal.componentInstance.cancelLabelKey = 'cancel';
            singleEnvironmentSelectModal.componentInstance.continueLabelKey = 'continue';
            singleEnvironmentSelectModal.componentInstance.continueBehavior = 'close_immediately';
            singleEnvironmentSelectModal.componentInstance.multipleSelect = false;
            singleEnvironmentSelectModal.result.then((selectedInstance: number) => {
                this.showGeoreferenceModal(selectedInstance, isViewGeoJSON);
            });
        });
    }

    showGeoreferenceModal(instanceId: number, isViewGeoJSON: boolean) {
        const geoReferenceModal = this.modalService.open(GeoReferenceModalComponent as Component, { size: 'lg', backdrop: 'static' });
        geoReferenceModal.componentInstance.studyId = this.studyEditorContext.studyId;
        geoReferenceModal.componentInstance.instanceId = instanceId;
        geoReferenceModal.componentInstance.programUUID = this.paramContext.programUUID;
        geoReferenceModal.componentInstance.cropName = this.paramContext.cropName;
        geoReferenceModal.componentInstance.isViewGeoJson = isViewGeoJSON;
    }

    exportStudyBook() {
        const modalRef = this.showChooseDatasetModalForExport();
        modalRef.result.then((result: DatasetModel) => {
            const exportStudyRef = this.modalService.open(ExportStudyModalComponent as Component, { size: 'md', backdrop: 'static' });
            exportStudyRef.componentInstance.datasetId = result.datasetId;
            exportStudyRef.result.then(() => {
            }, (reason) => {
                if (reason === 'back') {
                    this.exportStudyBook();
                }
            });
        });
    }

    exportStudyEntriesPrintingLabels() {
        this.studyEditorService.navigateToLabelPrintingView(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId, null, LabelPrintingType.STUDY_ENTRIES);
    }

    executeCalculatedVariables() {
        const modalRef = this.showChooseDatasetModalForExportExecuteCalculatedVariables();
        modalRef.result.then((selectedDataset: DatasetModel) => {
            const executeCalcRef = this.modalService.open(ExeculateCalculatedVariableModalComponent as Component, { size: 'md', backdrop: 'static' });
            executeCalcRef.componentInstance.datasetId = selectedDataset.datasetId;
            executeCalcRef.result.then(() => {
            }, (reason) => {
                if (reason === 'back') {
                    this.executeCalculatedVariables();
                }
            });
        });
    }

    showChooseDatasetModalForImport() {
        const plotDataset = this.studyEditorContext.getPlotDataset();
        const environmentDataset = this.studyEditorContext.getEnvironmentDataset();

        let supportedDatasetTypes = [DatasetTypeEnum.ENVIRONMENT, DatasetTypeEnum.STUDY_CONDITIONS];
        if (this.studyEditorContext.hasGeneratedDesign()) {
            supportedDatasetTypes = [...supportedDatasetTypes, ...DATASET_TYPES_OBSERVATION_IDS];
        }

        const modalRef = this.modalService.open(ChooseDatasetModalComponent as Component, { size: 'sm', backdrop: 'static' });
        modalRef.componentInstance.titleKey = 'study-editor.study-dataset.import-study-choose-dataset-modal.title';
        modalRef.componentInstance.descriptionKey = 'study-editor.study-dataset.import-study-choose-dataset-modal.description';
        modalRef.componentInstance.supportedDatasetTypes = supportedDatasetTypes;
        if (plotDataset) {
            modalRef.componentInstance.selectedDatasetType = plotDataset;
        } else if (environmentDataset) {
            modalRef.componentInstance.selectedDatasetType = environmentDataset;
        }
        return modalRef;
    }

    showChooseDatasetModalForExport() {
        const plotDataset = this.studyEditorContext.getPlotDataset();
        const environmentDataset = this.studyEditorContext.getEnvironmentDataset();

        let supportedDatasetTypes = [DatasetTypeEnum.ENVIRONMENT, DatasetTypeEnum.STUDY_CONDITIONS];
        if (this.studyEditorContext.hasGeneratedDesign()) {
            supportedDatasetTypes = [...supportedDatasetTypes, ...DATASET_TYPES_OBSERVATION_IDS];
        }

        const modalRef = this.modalService.open(ChooseDatasetModalComponent as Component, { size: 'sm', backdrop: 'static' });
        modalRef.componentInstance.titleKey = 'study-editor.study-dataset.export-study-modal.title';
        modalRef.componentInstance.descriptionKey = 'study-editor.study-dataset.export-study-choose-dataset-modal.description';
        modalRef.componentInstance.supportedDatasetTypes = supportedDatasetTypes;
        if (plotDataset) {
            modalRef.componentInstance.selectedDatasetType = plotDataset;
        } else if (environmentDataset) {
            modalRef.componentInstance.selectedDatasetType = environmentDataset;
        }
        return modalRef;
    }

    showChooseDatasetModalForExportExecuteCalculatedVariables() {
        const plotDataset = this.studyEditorContext.getPlotDataset();
        let supportedDatasetTypes;
        if (this.studyEditorContext.hasGeneratedDesign()) {
            supportedDatasetTypes = [...DATASET_TYPES_OBSERVATION_IDS];
        }

        const modalRef = this.modalService.open(ChooseDatasetModalComponent as Component, { size: 'sm', backdrop: 'static' });
        modalRef.componentInstance.titleKey = 'study-editor.study-dataset.execute-calculated-variable.choose-dataset-modal.title';
        modalRef.componentInstance.descriptionKey = 'study-editor.study-dataset.execute-calculated-variable.choose-dataset-modal.description';
        modalRef.componentInstance.supportedDatasetTypes = supportedDatasetTypes;
        if (plotDataset) {
            modalRef.componentInstance.selectedDatasetType = plotDataset;
        }
        return modalRef;
    }

    importDesign() {
        if (this.studyEditorContext.hasGeneratedDesign()) {
            this.alertService.error('study-editor.import-design.import-file.study-already-has-generated-design');
        } else {
            this.modalService.open(ImportDesignModalComponent as Component, { size: 'md', backdrop: 'static' });
        }
    }

    exportDesignTemplate() {
        this.experimentDesignService.exportDesignTemplate(this.studyEditorContext.studyId).toPromise().then(response => {
            const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
            this.fileDownloadHelper.save(response.body, fileName);
        });
    }

    createPlantingLabels() {
        const plotDataset = this.studyEditorContext.getPlotDataset();

        let supportedDatasetTypes = [];
        if (this.studyEditorContext.hasGeneratedDesign()) {
            supportedDatasetTypes = [...supportedDatasetTypes, ...DATASET_TYPES_OBSERVATION_IDS];
        }

        const modalRef = this.modalService.open(ChooseDatasetModalComponent as Component, { size: 'sm', backdrop: 'static' });
        modalRef.componentInstance.titleKey = 'study-editor.create-planting-labels.choose-dataset-title';
        modalRef.componentInstance.descriptionKey = 'study-editor.create-planting-labels.choose-dataset-description';
        modalRef.componentInstance.supportedDatasetTypes = supportedDatasetTypes;
        if (plotDataset) {
            modalRef.componentInstance.selectedDatasetType = plotDataset;
        }
        modalRef.result.then((result: DatasetModel) => {
            if (result) {
                if (result.datasetTypeId === DatasetTypeEnum.PLOT) {
                    this.studyEditorService.navigateToLabelPrintingView(this.studyEditorContext.studyId, result.datasetId, null, LabelPrintingType.OBSERVATION_DATASET);
                } else {
                    this.studyEditorService.navigateToLabelPrintingView(this.studyEditorContext.studyId, result.datasetId, null, LabelPrintingType.SUBOBSERVATION_DATASET);
                }
            }
        });
    }

    exportCrossingTemplate() {
        const request = this.crossService.downloadCrossingTemplate(this.studyEditorContext.studyId).toPromise().then(response => {
            const fileName =  'CrossingTemplate-' + this.studyEditorContext.studyDetails.name + '.xlsx';
            this.fileDownloadHelper.save(response.body, fileName);
        });
    }

    importCrosses() {
        this.modalService.open(ImportCrossesModalComponent as Component, { size: 'md', backdrop: 'static' });
    }

    designCrosses() {
        this.studyEditorService.navigateToDesignCrosses(this.studyEditorContext.studyId, this.studyName);
    }

    createSubObservationUnit() {
        const numberOfExistingSubObs = this.studyEditorContext.observationDatasets.filter((dataset: DatasetModel) => this.SUB_OBSERVATION_DATASETS.includes(dataset.datasetTypeId)).length;
        if (numberOfExistingSubObs >= this.studyEditorContext.maxSubObservationUnitSets) {
            this.alertService.error('study-editor.sub-observation-dataset-builder.error.study.subobs.limit', { param: this.studyEditorContext.maxSubObservationUnitSets });
        } else {
            this.modalService.open(SubObservationUnitTypeSelectorModalComponent as Component, { size: 'md', backdrop: 'static' });
        }
    }

    advanceStudy() {
        this.advanceStudyModalService.advanceStudy();
    }

    advanceSampledPlants() {
        this.studyService.studyHasSamples(this.studyId).toPromise().then((response)=> {
            if (response && response.body) {
                this.advanceStudyModalService.advanceSampledPlants();
            } else {
                this.alertService.error('study-editor.advance-sample.error.no-sampled-plants');
            }
        });

    }

    changeLockedStatus(lockedStatus: 0 | 1) {
        if (this.userHasLockPermission() && this.principal.hasAnyAuthorityDirect(LOCK_STUDY_PERMISSIONS)) {
            const studyStatusRequest = {
                'locked': lockedStatus
            };
            this.studyService.changeLockedStatus(this.studyEditorContext.studyId, studyStatusRequest).toPromise().then(response => {
                if (lockedStatus) {
                    this.alertService.success('study-editor.lock.study.success.message');
                } else {
                    this.alertService.success('study-editor.unlock.study.success.message');
                }
                this.studyEditorContext.studyDetails.locked = lockedStatus === 1;
            });
        }
    }

    deleteStudy() {
        const modalConfirmRef = this.modalService.open(ModalConfirmComponent as Component);
        modalConfirmRef.componentInstance.title = this.translateService.instant('study-editor.delete.study.confirmation.title');
        modalConfirmRef.componentInstance.message =  this.translateService.instant('study-editor.delete.study.confirmation', { param: this.studyName });
        modalConfirmRef.componentInstance.showButtonIcons = false;
        modalConfirmRef.result.then(() => {
            this.studyService.deleteStudy(this.studyEditorContext.studyId).toPromise().then(() => {
                this.alertService.success('study-editor.delete.study.success');
                this.navigateToManagerStudies();
            });
        });

    }

    async createLots() {
        try {
            await this.studyEditorService.navigateToDatasetTabNoReload(this.studyId, this.studyEditorContext.getPlotDataset().datasetId);
            // Wait for next macrotask to allow component initialization
            await new Promise(resolve => setTimeout(resolve));
            this.studyDatasetContext.triggerCreateLots();
        } catch (error) {
            console.error('Error creating lots:', error);
        }
    }

    async createPreparePlantingInventory() {
        try {
            await this.studyEditorService.navigateToDatasetTabNoReload(this.studyId, this.studyEditorContext.getPlotDataset().datasetId);
            // Wait for next macrotask to allow component initialization
            await new Promise(resolve => setTimeout(resolve));
            this.studyDatasetContext.triggerPlantingInventory();
        } catch (error) {
            console.error('Error preparing inventory:', error);
        }
    }

    async changePlotEntry() {
        try {
            await this.studyEditorService.navigateToDatasetTabNoReload(this.studyId, this.studyEditorContext.getPlotDataset().datasetId);
            // Wait for next macrotask to allow component initialization
            await new Promise(resolve => setTimeout(resolve));
            this.studyDatasetContext.triggerChangePlotEntry();
        } catch (error) {
            console.error('Error change plot entry:', error);
        }
    }

    createGenotypingSamples() {
        this.genotypingSamplesModalService.createGenotypingSamples();
    }

    navigateToManagerStudies(studyId?: number, studyName?: string) {
        this.router.navigate(['', 'study-manager'], { queryParamsHandling: 'merge', queryParams: { studyId: null, studyName: null } });
    }

    analyzeWithStaBrappAction() {
        this.analyzeWithBrapp(CropParameterTypeEnum.STA_BRAPP, this.staBrappURL);
    }

    analyzeWithDecisionSupportAction() {
        this.analyzeWithBrapp(CropParameterTypeEnum.DECISION_SUPPORT_BRAPP, this.dsBrappURL);
    }

    analyzeWithTrialDataExplorerAction() {
        this.analyzeWithBrapp(CropParameterTypeEnum.TDE_BRAPP_URL, this.tdeExplorerBrapp);
    }

    analyzeWithBrapp(type: CropParameterTypeEnum, brappURL: string) {

        this.datasetService.getDataset(this.studyEditorContext.studyId, this.studyEditorContext.getPlotDataset().datasetId).toPromise().then((dataset) => {
            const chooseEnvironmentModal = this.modalService.open(ChooseEnvironmentModalComponent as Component, { size: 'md', backdrop: 'static' });
            chooseEnvironmentModal.componentInstance.studyInstanceItems = dataset.body.instances;
            if (type === CropParameterTypeEnum.TDE_BRAPP_URL) {
                chooseEnvironmentModal.componentInstance.titleKey = 'study-editor.brapp.modal-select-environment.tde-brapp.title';
            } else if (type === CropParameterTypeEnum.STA_BRAPP) {
                chooseEnvironmentModal.componentInstance.titleKey = 'study-editor.brapp.modal-select-environment.sta-brapp.title';
            } else if (type === CropParameterTypeEnum.DECISION_SUPPORT_BRAPP) {
                chooseEnvironmentModal.componentInstance.titleKey = 'study-editor.brapp.modal-select-environment.ds-brapp.title';
            }
            chooseEnvironmentModal.componentInstance.headerKey = 'study-editor.brapp.modal-select-environment.header';
            chooseEnvironmentModal.componentInstance.descriptionKey = 'study-editor.brapp.modal-select-environment.description';
            chooseEnvironmentModal.componentInstance.cancelLabelKey = 'cancel';
            chooseEnvironmentModal.componentInstance.continueLabelKey = 'proceed';
            chooseEnvironmentModal.componentInstance.continueBehavior = 'close_immediately';
            chooseEnvironmentModal.result.then((studyInstanceItems: StudyInstanceItem[]) => {
                const instanceIds = studyInstanceItems
                    .filter((studyInstanceItem) => studyInstanceItem.isChecked)
                    .map((studyInstanceItem) => studyInstanceItem.instanceId);
                window.open(brappURL +
                    '?cropDb=' + this.paramContext.cropName +
                    '&apiURL=' + window.location.origin + '/bmsapi' +
                    '&studyDbIds=' + instanceIds
                , '_blank');
            });
        });
    };

    /************ PERMISSIONS *****************/

    showStudyAction() {
        return this.principal.hasAnyAuthorityDirect(CLOSE_STUDY_PERMISSIONS) ||
            this.principal.hasAnyAuthorityDirect(DELETE_STUDY_PERMISSIONS) ||
            this.showDesignAndPlanningOptions() ||
            this.showCrossingOptionsAction() ||
            this.showSubObservationUnitOptionsAction() ||
            this.showFieldMapOptionsAction() ||
            this.showDataCollectionAction() ||
            this.displayExecuteCalculatedVariableOnlyActions() ||
            this.showCreateGenotypingSamplesAction() ||
            this.showAdvancesAction() ||
            this.showCreateLotsAction() ||
            this.showLockStudyAction() ||
            this.showUnlockStudyAction() ||
            this.showAnalyzeWithStaBrappAction() ||
            this.showAnalyzeWithDecisionSupportAction();
    }

    showCrossingOptionsAction() {
        return this.showExportCrossingTemplateAction() ||
            this.showImportCrossesAction() ||
            this.showDesignNewCrossesAction();
    }

    showCreateSubObservationUnitsAction() {
        return this.principal.hasAnyAuthorityDirect(CREATE_SUB_OBSERVATION_UNITS_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showChangePlotEntryAction() {
        return this.principal.hasAnyAuthorityDirect(CHANGE_PLOT_ENTRY_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showSubObservationUnitOptionsAction() {
        return this.showCreateSubObservationUnitsAction() || this.showChangePlotEntryAction();
    }

    showExportStudyBookAction() {
        return this.principal.hasAnyAuthorityDirect(EXPORT_STUDY_BOOK_PERMISSIONS);
    }

    showExportStudyEntriesAction() {
        return this.principal.hasAnyAuthorityDirect(EXPORT_STUDY_ENTRIES_PERMISSIONS) && this.studyEditorContext.hasGermplasmList();
    }

    showImportObservationsAction() {
        return this.principal.hasAnyAuthorityDirect(MANAGE_PENDING_OBSERVATION_VALUES_PERMISSIONS);
    }

    showDataCollectionAction() {
        return this.showExportStudyBookAction() || //
            this.showImportObservationsAction() || //
            this.showExportStudyEntriesAction(); //
    }

    showCreateGenotypingSamplesAction() {
        return this.principal.hasAnyAuthorityDirect(CREATE_GENOTYPING_SAMPLES_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showAdvancesAction() {
        return this.principal.hasAnyAuthorityDirect(ADVANCES_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showAdvanceStudyAction() {
        return this.principal.hasAnyAuthorityDirect(ADVANCE_STUDY_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showAdvanceStudyForPlantsAction() {
        return this.principal.hasAnyAuthorityDirect(ADVANCE_STUDY_FOR_PLANTS_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    }

    showAnalyzeWithStaBrappAction() {
        return this.staBrappURL && this.principal.hasAnyAuthorityDirect(ANALYZE_WITH_STA_BRAPP_PERMISSIONS);
    }

    showAnalyzeWithDecisionSupportAction() {
        return this.dsBrappURL && this.principal.hasAnyAuthorityDirect(ANALYZE_WITH_DECISION_SUPPORT_PERMISSIONS);
    }

    showTrialDataExplorerAction() {
        return this.tdeExplorerBrapp && this.principal.hasAnyAuthorityDirect(ANALYZE_WITH_TRIALDATAEXP_BRAPP_PERMISSIONS);
    }

    showFieldMapOptionsAction() {
        return this.showMakeFieldMapAction() || //
            this.showViewFieldMapAction() || //
            this.showDeleteFieldMapAction() || //
            this.showCreateGeoreferenceAction() || //
            this.showEditGeoreferenceAction();
    }

    closeSampleListTab(event, listId: number) {
        event.stopPropagation();
        event.preventDefault();
        this.studyEditorContext.sampleLists = this.studyEditorContext.sampleLists.filter((sampleList) => sampleList.listId !== listId);
    }

    showMakeFieldMapAction() {
        return this.principal.hasAnyAuthorityDirect(MAKE_FIELD_MAP_PERMSISSION) && this.studyEditorContext.hasGeneratedDesign();
    }

    showViewFieldMapAction() {
        return this.principal.hasAnyAuthorityDirect(VIEW_FIELD_MAP_PERMSISSION) && this.studyEditorContext.hasGeneratedDesign();
    }

    showDeleteFieldMapAction() {
        return this.principal.hasAnyAuthorityDirect(DELETE_FIELD_MAP_PERMSISSION) && this.studyEditorContext.hasGeneratedDesign();
    }

    showCreateGeoreferenceAction() {
        return this.principal.hasAnyAuthorityDirect(CREATE_GEOREFERENCE_PERMSISSION) && this.studyEditorContext.hasGeneratedDesign();
    }

    showEditGeoreferenceAction() {
        return this.principal.hasAnyAuthorityDirect(EDIT_GEOREFERENCE_PERMSISSION) && this.studyEditorContext.hasGeneratedDesign();
    }

    showExportCrossingTemplateAction() {
        return this.principal.hasAnyAuthorityDirect(EXPORT_CROSSING_TEMPLATE_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    };

    showImportCrossesAction() {
        return this.principal.hasAnyAuthorityDirect(IMPORT_CROSSES_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    };

    showDesignNewCrossesAction() {
        return this.principal.hasAnyAuthorityDirect(DESIGN_NEW_CROSSES_PERMISSIONS) && this.studyEditorContext.hasGeneratedDesign();
    };

    showPreparePlantingInventoryAction(): boolean {
        return this.studyEditorContext.hasGeneratedDesign() && this.principal.hasAnyAuthorityDirect(PREPARE_INVENTORY_PERMISSIONS);
    }

    showPrintingLabelsAction(): boolean {
        return this.studyEditorContext.hasGeneratedDesign && this.principal.hasAnyAuthorityDirect(CREATE_PLANTING_LABELS_PERMISSIONS);
    }

    showImportOwnDesignAction(): boolean {
        return this.studyEditorContext.hasGermplasmList() && this.principal.hasAnyAuthorityDirect(IMPORT_DESIGN_PERMISSIONS);
    }

    showExportDesignTemplateAction(): boolean {
        return this.principal.hasAnyAuthorityDirect(EXPORT_DESIGN_TEMPLATE_PERMISSIONS);
    }

    showLockStudyAction(): boolean {
        return !this.studyEditorContext.isLockedStudy() && this.userHasLockPermission() && this.principal.hasAnyAuthorityDirect(LOCK_STUDY_PERMISSIONS);
    }

    showUnlockStudyAction(): boolean {
        return this.studyEditorContext.isLockedStudy() && this.userHasLockPermission() && this.principal.hasAnyAuthorityDirect(LOCK_STUDY_PERMISSIONS);
    }

    showDesignAndPlanningOptions(): boolean {
        return this.showExportDesignTemplateAction() ||
            this.showImportOwnDesignAction() ||
            this.showPrintingLabelsAction() ||
            this.showPreparePlantingInventoryAction();
    }

    showCreateLotsAction(): boolean {
        return this.studyEditorContext.hasGeneratedDesign() &&
            this.principal.hasAnyAuthorityDirect(CREATE_LOTS_PERMISSIONS);
    }

    displayExecuteCalculatedVariableOnlyActions(): boolean {
        return this.principal.hasAnyAuthority(EXECUTE_CALCULATED_VARIABLES_PERMISSIONS) &&
            this.studyEditorContext.isStudyHasCalculatedVariables() &&
            this.studyEditorContext.hasGeneratedDesign();
    }

    showCloseStudyAction(): boolean {
        return this.principal.hasAnyAuthorityDirect(CLOSE_STUDY_PERMISSIONS);
    }

    showDeleteStudyAction(): boolean {
        // If the user is not the owner of the study and doesn't have DELETE_STUDY_NOT_OWNED permission, do not display the Delete Action.
        if (!this.isCurrentUserOwnerOfStudy() && !this.principal.hasAnyAuthorityDirect(DELETE_STUDY_NOT_OWNED_PERMISSIONS)) {
            return false;
        }
        // Else check for delete permissions
        return this.principal.hasAnyAuthorityDirect(DELETE_STUDY_PERMISSIONS) || this.principal.hasAnyAuthorityDirect(DELETE_STUDY_NOT_OWNED_PERMISSIONS);
    }

    userHasLockPermission(): boolean {
        return this.isSuperAdmin() || this.isCurrentUserOwnerOfStudy() || this.principal.hasAnyAuthorityDirect(LOCK_STUDY_NOT_OWNED_PERMISSIONS);
    };

    isSuperAdmin() {
        if (this.identity) {
            return this.identity.authorities.includes('ADMIN') || this.identity.userRoles.some(userRole => userRole.role.name.toLowerCase() === 'superadmin');
        }
        return false;
    }

    isCurrentUserOwnerOfStudy() {
        if (this.identity) {
            return this.identity?.id === this.studyEditorContext?.studyDetails?.ownerId;
        }
        return false;
    }

    async showCrossesAndSelectionsTab() {
        const response = await this.germplasmStudySourceService.searchGermplasmStudySources(this.studyId, {
            studyId: this.studyId,
            filter: null
        }, 0, 1, null).toPromise();
        if (response.totalCount > 0) {
            return true;
        }
        return undefined;
    }

    async showSampleGenotypesTab() {
        const response: PaginatedResponse<SampleGenotypeModel> = await this.sampleGenotypeService.searchSampleGenotypes(this.studyId, {}, 0, 1, '').toPromise();
        if (response.totalCount > 0) {
            return true;
        }
        return false;
    }

    async showInventoryTab() {
        const response = await this.inventoryService.searchStudyTransactions(this.studyId, {}, 0, 1, '').toPromise();
        if (response.totalCount > 0) {
            return true;
        }
        return false;
    }

    showStudySettingsTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_STUDY_SETTINGS_PERMISSIONS);
    }

    showAnalysisTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_SINGLE_SITE_ANALYSIS_RESULTS_PERMISSIONS) && (this.studyEditorContext.hasMeansDataset() || this.studyEditorContext.hasSummaryStatisticsDataset());
    }

    showGermplasmAndChecksTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_GERMPLASM_AND_CHECKS_PERMISSIONS);
    }

    showTreatmentFactorsTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_TREATMENT_FACTORS_PERMISSIONS);
    }

    showEnvironmentsTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_ENVIRONMENT_PERMISSIONS);
    }

    showExperimentalDesignTab() {
        return this.principal.hasAnyAuthorityDirect(VIEW_EXPERIMENTAL_DESIGN_PERMISSIONS);
    }

}
